import * as React from "react";
import classnames from "classnames";
import { Tag } from "@grandvisionhq/common";
import { Heading, Text } from "@grandvisionhq/elements";
import { Icon } from "@grandvisionhq/icons";
import { Skeleton } from "@grandvisionhq/iris-ui";
import { useIntl } from "@grandvisionhq/state";
import AddressContainer from "../../../Address/components/Address/AddressContainer.js";
import { LocationVariant } from "./types.js";
/* empty css              */
const LocationLoadingImg = (props) => /* @__PURE__ */ React.createElement("svg", { ...props, fill: "none", xmlns: "http://www.w3.org/2000/svg", height: "280" }, /* @__PURE__ */ React.createElement("rect", { width: "100%", height: "30", rx: "5", fill: "currentColor", y: "10" }), /* @__PURE__ */ React.createElement("rect", { width: "40%", height: "50", rx: "5", fill: "currentColor", y: "60" }), /* @__PURE__ */ React.createElement("rect", { width: "100%", height: "60", rx: "5", fill: "currentColor", y: "120" }), /* @__PURE__ */ React.createElement("rect", { width: "60%", height: "30", rx: "5", fill: "currentColor", y: "190" }), /* @__PURE__ */ React.createElement("rect", { width: "100%", height: "40", rx: "5", fill: "currentColor", y: "230" }));
const DefaultLocationVariant = LocationVariant.ListItem;
const Location = ({
  className,
  id,
  variant = DefaultLocationVariant,
  name,
  addressSize,
  addressData,
  emailAddress,
  phone,
  phoneHidden,
  emailHidden,
  highlight = false,
  distanceDetails = null,
  formattedDistance,
  note = null,
  buttons = null,
  openingHours,
  locationDetailsUrl,
  wheelchairAccessible,
  hearingAppointmentPossible,
  isFranchiseStore,
  loading = false,
  headerImage,
  closeInfoWindow,
  onNameClick,
  // @deprecated
  formattedAddress,
  // @deprecated
  ...rest
}) => {
  const { getLabel } = useIntl();
  const showFormattedAddress = formattedAddress && (!addressSize || !addressData);
  const showFormattedDistanceBelowAddress = !!(formattedDistance && addressSize !== "MINIMAL");
  const showPhone = !phoneHidden && phone;
  const showEmailAddress = !emailHidden && emailAddress;
  const showTags = wheelchairAccessible || hearingAppointmentPossible || isFranchiseStore;
  return /* @__PURE__ */ React.createElement(
    "article",
    {
      ...rest,
      id: id !== void 0 ? `${id}` : id,
      className: classnames(
        "location-item",
        {
          "location-item--highlight": highlight,
          "location-item--loading": loading,
          "location-item--list-item": variant === LocationVariant.ListItem,
          "location-item--stand-alone": variant === LocationVariant.StandAlone,
          "location-item--map-item": variant === LocationVariant.MapItem
        },
        className
      )
    },
    loading ? /* @__PURE__ */ React.createElement("div", { className: "location-item__skeleton-loader" }, /* @__PURE__ */ React.createElement(Skeleton, { height: "100%", width: "100%" }, /* @__PURE__ */ React.createElement(LocationLoadingImg, { "aria-label": getLabel("common.loadingText") }))) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", { className: "location-item__part" }, /* @__PURE__ */ React.createElement("div", { className: "location-item__content" }, headerImage?.url && /* @__PURE__ */ React.createElement(
      "img",
      {
        className: "location-item__header-image",
        alt: headerImage.description || "",
        src: headerImage.url
      }
    ), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(
      Heading,
      {
        className: "location-item__heading",
        component: variant === LocationVariant.ListItem || variant === LocationVariant.MapItem ? "h2" : "h1",
        variant: variant === LocationVariant.ListItem ? "smallest" : "small"
      },
      renderHeadingContent()
    ), closeInfoWindow ? /* @__PURE__ */ React.createElement(
      "div",
      {
        className: "store-finder-map__active-location-close-button",
        "data-t": "close-button"
      },
      /* @__PURE__ */ React.createElement("button", { onClick: closeInfoWindow, type: "button" }, /* @__PURE__ */ React.createElement(Icon, { name: "close" }))
    ) : null), showTags && /* @__PURE__ */ React.createElement("div", { className: "location-item__tags" }, hearingAppointmentPossible && /* @__PURE__ */ React.createElement(
      Tag,
      {
        label: getLabel("stores.page.details.hearingAppointmentPossibleShort")
      }
    ), wheelchairAccessible && /* @__PURE__ */ React.createElement(Tag, { label: getLabel("stores.page.details.wheelchairAccessible") }), isFranchiseStore && /* @__PURE__ */ React.createElement(
      Tag,
      {
        label: getLabel("locations.component.storeTileIcons.franchiseStoreShort")
      }
    )), /* @__PURE__ */ React.createElement("div", { className: "location-item__address", "data-t": "location-item-address" }, showFormattedAddress ? /* @__PURE__ */ React.createElement("address", null, formattedAddress) : /* @__PURE__ */ React.createElement(
      AddressContainer,
      {
        size: addressSize,
        data: addressData,
        prepend: formattedDistance && addressSize === "MINIMAL" ? `${formattedDistance} · ` : void 0
      },
      showFormattedDistanceBelowAddress || showPhone || showEmailAddress ? /* @__PURE__ */ React.createElement("div", { className: "location-item__contact" }, showFormattedDistanceBelowAddress ? /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Text, { component: "div" }, formattedDistance)) : null, showPhone && /* @__PURE__ */ React.createElement("div", { className: "location-item__contact-field" }, /* @__PURE__ */ React.createElement("a", { href: `tel:${phone}`, "data-t": "Phone" }, phone)), showEmailAddress && /* @__PURE__ */ React.createElement("div", { className: "location-item__contact-field" }, /* @__PURE__ */ React.createElement("a", { href: `mailto:${emailAddress}`, "data-t": "Email" }, emailAddress))) : null
    ))), distanceDetails && /* @__PURE__ */ React.createElement("div", { className: "location-item__distance-details" }, distanceDetails)), note, variant === LocationVariant.StandAlone && /* @__PURE__ */ React.createElement("div", { className: "location-item__actions" }, buttons)), (openingHours || buttons) && /* @__PURE__ */ React.createElement("div", { className: "location-item__part" }, openingHours && /* @__PURE__ */ React.createElement("div", { className: "location-item__opening-hours" }, openingHours), buttons && (variant === LocationVariant.ListItem || variant === LocationVariant.MapItem) && /* @__PURE__ */ React.createElement("div", { className: "location-item__actions" }, buttons)))
  );
  function renderHeadingContent() {
    if (locationDetailsUrl) {
      return /* @__PURE__ */ React.createElement(
        "a",
        {
          "data-t": `location-link-${id}`,
          className: "location-item__heading-link",
          href: locationDetailsUrl
        },
        name
      );
    }
    if (onNameClick) {
      return /* @__PURE__ */ React.createElement(
        "button",
        {
          "data-t": `location-link-${id}`,
          className: "location-item__link",
          onClick: () => onNameClick(id)
        },
        name
      );
    }
    return name;
  }
};
export {
  Location as default
};
//# sourceMappingURL=Location.js.map
